// src/components/merchant/ServiceLocationPicker.js
// ============================================================
// SELECTEUR DE LIEU DE PRESTATION (saisie unique)
// ------------------------------------------------------------
// Le marchand enregistre son adresse + ses horaires UNE SEULE FOIS
// (Parametres > Lieux de prestation). Ici il se contente de choisir
// le lieu : adresse exacte, GPS, horaires et consignes sont remplis
// automatiquement sur l'annonce.
// Maxula
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { FiMapPin, FiSettings, FiLoader } from 'react-icons/fi';
import { merchantAPI } from '../../services/api';

const MANUAL = '__manual__';

const ServiceLocationPicker = ({
  value = '',            // serviceLocationId courant ('' = saisie manuelle)
  onApply,               // (locationOrNull) => void — le parent remplit son formulaire
  autoSelectDefault = false, // true en creation : preselectionne le lieu par defaut
  disabled = false
}) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const autoDone = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await merchantAPI.getServiceLocations();
        const list = res?.data?.data || [];
        if (!alive) return;
        setLocations(Array.isArray(list) ? list : []);
      } catch (e) {
        if (alive) setLocations([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Preselection du lieu par defaut a la creation d'une annonce.
  useEffect(() => {
    if (!autoSelectDefault || autoDone.current || loading || !locations.length) return;
    if (value) { autoDone.current = true; return; }
    const def = locations.find((l) => l.isDefault) || locations[0];
    if (def && typeof onApply === 'function') {
      autoDone.current = true;
      onApply(def);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, locations, autoSelectDefault]);

  const handleChange = (e) => {
    const v = e.target.value;
    if (v === MANUAL) return onApply && onApply(null);
    const loc = locations.find((l) => String(l.id) === String(v));
    if (loc && onApply) onApply(loc);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
        <FiLoader className="animate-spin" size={14} /> Chargement de vos lieux de prestation…
      </div>
    );
  }

  if (!locations.length) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <p className="text-sm text-blue-900 flex items-start gap-2">
          <FiMapPin className="mt-0.5 flex-shrink-0" size={15} />
          <span>
            Vous n'avez pas encore de lieu enregistré. Renseignez l'adresse et les horaires
            ci-dessous, puis cochez <strong>« Enregistrer ce lieu dans mon profil »</strong> :
            vos prochaines annonces seront pré-remplies automatiquement.
          </span>
        </p>
      </div>
    );
  }

  const selected = locations.find((l) => String(l.id) === String(value));

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <label className="block text-sm font-medium mb-1 flex items-center gap-2">
        <FiMapPin size={14} /> Lieu de prestation enregistré
      </label>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <select
          value={selected ? String(selected.id) : MANUAL}
          onChange={handleChange}
          disabled={disabled}
          className="w-full px-4 py-2 border rounded-lg bg-white disabled:bg-gray-100"
        >
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}{l.isDefault ? ' (par défaut)' : ''} — {l.addressFull}
            </option>
          ))}
          <option value={MANUAL}>Saisie manuelle (adresse spécifique à cette annonce)</option>
        </select>
        <a
          href="/merchant/settings"
          className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          title="Gérer mes lieux de prestation"
        >
          <FiSettings size={14} /> Gérer
        </a>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {selected
          ? 'Adresse, GPS, horaires et consignes sont repris de ce lieu. Si vous le modifiez dans vos paramètres, toutes les annonces qui l\'utilisent sont mises à jour automatiquement.'
          : 'Saisie manuelle : les informations ci-dessous ne concernent que cette annonce.'}
      </p>
    </div>
  );
};

export default ServiceLocationPicker;
